import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaArrowLeft, FaClock, FaUser, FaTag, FaCalendar, FaSearch, FaList, FaBookmark } from 'react-icons/fa';
import SocialShare from '../components/SocialShare';
import VoteButtons from '../components/VoteButtons';

interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  banner: string;
  author: string;
  category: string;
  tags: string[];
  excerpt: string;
  content: string;
  date: string;
  metadata?: {
    readTime?: number;
  };
}

const BlogPost: React.FC = () => {
  const { slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeHeading, setActiveHeading] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);

  const API_URL = import.meta.env.VITE_API_BACKEND_URL;
  const MAIN_SITE = import.meta.env.VITE_MAIN_SITE_URL || 'https://www.nostrahealth.com';

  // Extract headings from markdown content for TOC
  const tableOfContents = useMemo(() => {
    if (!post?.content) return [];

    const headings: HeadingItem[] = [];
    const lines = post.content.split('\n');

    lines.forEach((line) => {
      const match = line.match(/^(#{1,3})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        headings.push({ id, text, level });
      }
    });

    return headings;
  }, [post?.content]);

  // Filter posts based on search term
  const filteredPosts = useMemo(() => {
    if (!searchTerm.trim()) return allPosts;

    const term = searchTerm.toLowerCase();
    return allPosts.filter(p =>
      p.title.toLowerCase().includes(term) ||
      p.category.toLowerCase().includes(term) ||
      p.author.toLowerCase().includes(term) ||
      p.tags?.some(tag => tag.toLowerCase().includes(term))
    );
  }, [allPosts, searchTerm]);

  useEffect(() => {
    if (slug) {
      fetchPost();
    } else {
      setError(true);
      setLoading(false);
    }
    fetchAllPosts();
  }, [slug]);

  const fetchAllPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/blogposts`);
      const data = await res.json();
      const postsArray = Array.isArray(data) ? data : (data.data || []);
      setAllPosts(postsArray.slice(0, 10)); // Show top 10 posts
    } catch (err) {
      console.error('Error fetching all posts:', err);
    }
  };

  // Track active heading for TOC highlighting with scroll events
  useEffect(() => {
    if (!tableOfContents.length || !post) return;

    const handleScroll = () => {
      const headingElements = tableOfContents.map(({ id }) => {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          return {
            id,
            top: rect.top,
            bottom: rect.bottom,
          };
        }
        return null;
      }).filter(Boolean);

      // Find the heading that's currently in the viewport (closest to top of screen)
      const viewportMiddle = window.innerHeight / 3; // Check upper third of viewport
      let activeId = '';

      for (let i = headingElements.length - 1; i >= 0; i--) {
        const heading = headingElements[i];
        if (heading && heading.top <= viewportMiddle) {
          activeId = heading.id;
          break;
        }
      }

      if (activeId) {
        setActiveHeading(activeId);
      }
    };

    // Initial check
    handleScroll();

    // Add scroll listener with throttle
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollListener, { passive: true });

    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [tableOfContents, post]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(false);
      const res = await fetch(`${API_URL}/blogpost/${slug}`);
      if (!res.ok) throw new Error('Post not found');
      const data = await res.json();
      setPost(data);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-Color"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white flex items-center justify-center">
        <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-12 border border-white/40 shadow-glass-hover text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-Color to-brightColor text-white px-8 py-3 rounded-full font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <FaArrowLeft />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | NostraHealth Blog</title>
        <meta name="description" content={post.excerpt || post.title} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://blogs.nostrahealth.com/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        {post.banner && <meta property="og:image" content={post.banner} />}
        <meta property="og:site_name" content="NostraHealth Blog" />
        <meta property="article:author" content={post.author} />
        <meta property="article:published_time" content={post.date} />
        {post.tags && post.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://blogs.nostrahealth.com/${post.slug}`} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        {post.banner && <meta name="twitter:image" content={post.banner} />}

        <link rel="canonical" href={`https://blogs.nostrahealth.com/${post.slug}`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white">
        {/* Header */}
        <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50 shadow-lg">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-Color to-brightColor bg-clip-text text-transparent hover:from-hoverColor hover:to-Color transition-all">
                NostraHealth <span className="text-sm font-normal text-gray-600">/ Blog</span>
              </Link>
              <nav className="flex items-center gap-6">
                <Link to="/" className="text-gray-700 hover:text-Color font-medium transition-colors flex items-center gap-2">
                  <FaArrowLeft size={14} />
                  All Posts
                </Link>
                <a href={MAIN_SITE} className="text-gray-700 hover:text-Color font-medium transition-colors">
                  Home
                </a>
                <a href={import.meta.env.VITE_DOCS_URL} className="text-gray-700 hover:text-Color font-medium transition-colors">
                  Documentation
                </a>
              </nav>
            </div>
          </div>
        </header>

        {/* Main Layout with Sidebars */}
        <div className="max-w-[1800px] mx-auto px-4 py-12">
          <div className="flex gap-2">
            {/* Left Sidebar - Search & Filters */}
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Search */}
                <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaSearch className="text-Color" />
                    Search
                  </h3>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search content..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 rounded-xl backdrop-blur-xl bg-white/80 border border-white/40 text-gray-900 text-sm focus:outline-none focus:border-Color/50 transition-all placeholder-gray-500"
                    />
                  </div>
                </div>

                {/* Quick Links */}
                <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaList className="text-Color" />
                    Quick Links
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    <Link
                      to="/"
                      className="block px-4 py-2 rounded-xl bg-white/40 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all text-sm font-medium"
                    >
                      All Posts
                    </Link>
                    {filteredPosts.map((p) => (
                      <Link
                        key={p.id}
                        to={`/${p.slug}`}
                        className={`block px-4 py-2 rounded-xl transition-all text-sm font-medium ${
                          p.slug === slug
                            ? 'bg-gradient-to-r from-Color to-brightColor text-white'
                            : 'bg-white/40 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white'
                        }`}
                      >
                        {p.title}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Category */}
                {post && (
                  <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                    <h3 className="text-sm font-bold text-gray-600 mb-2">Category</h3>
                    <div className="px-4 py-2 bg-gradient-to-r from-Color to-brightColor text-white rounded-xl text-sm font-bold text-center">
                      {post.category}
                    </div>
                  </div>
                )}
              </div>
            </aside>

            {/* Main Content */}
            <article className="flex-1 min-w-0">
              <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-8 md:p-10 border border-white/40 shadow-2xl">
            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-block px-5 py-2 bg-gradient-to-r from-Color via-brightColor to-accentPurple text-white rounded-full text-sm font-bold shadow-glass">
                {post.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-5xl font-black text-gray-900 mb-6 leading-tight bg-gradient-to-r from-darkPurple to-Color bg-clip-text text-transparent">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-gray-600">
              <div className="flex items-center gap-2 backdrop-blur-xl bg-white/50 px-4 py-2 rounded-full border border-white/40 shadow-glass">
                <FaUser className="text-Color" />
                <span className="font-medium">{post.author}</span>
              </div>
              <div className="flex items-center gap-2 backdrop-blur-xl bg-white/50 px-4 py-2 rounded-full border border-white/40 shadow-glass">
                <FaCalendar className="text-Color" />
                <span className="font-medium">{new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              <div className="flex items-center gap-2 backdrop-blur-xl bg-white/50 px-4 py-2 rounded-full border border-white/40 shadow-glass">
                <FaClock className="text-Color" />
                <span className="font-medium">{post.metadata?.readTime || 5} min read</span>
              </div>
            </div>

            {/* Featured Image */}
            {post.banner && (
              <div className="mb-12">
                <div className="relative overflow-hidden rounded-3xl shadow-glass-hover group">
                  <div className="absolute inset-0 bg-gradient-to-t from-darkPurple/70 via-darkPurple/30 to-transparent z-10"></div>
                  <img
                    src={post.banner}
                    alt={post.title}
                    className="w-full h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            )}

            {/* Excerpt */}
            {post.excerpt && (
              <div className="text-xl text-gray-700 italic mb-12 pb-8 border-b border-white/40 font-medium">
                {post.excerpt}
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children, ...props }) => {
                  const text = String(children);
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h1 id={id} {...props}>{children}</h1>;
                },
                h2: ({ children, ...props }) => {
                  const text = String(children);
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h2 id={id} {...props}>{children}</h2>;
                },
                h3: ({ children, ...props }) => {
                  const text = String(children);
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h3 id={id} {...props}>{children}</h3>;
                },
                code({ node, className, children, ...props }: any) {
                  const match = /language-(\w+)/.exec(className || '');
                  const inline = (props as any).inline;
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus as any}
                      language={match[1]}
                      PreTag="div"
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Vote and Share Section */}
          <div className="mt-12 pt-8 border-t border-white/40">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Was this post helpful?</h4>
                <VoteButtons postId={post.id} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Share this post</h4>
                <SocialShare
                  url={`https://blogs.nostrahealth.com/${post.slug}`}
                  title={post.title}
                  description={post.excerpt}
                  postId={post.id}
                />
              </div>
            </div>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/40">
              <div className="flex items-center gap-2 flex-wrap">
                <FaTag className="text-Color" />
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-4 py-2 backdrop-blur-xl bg-white/60 text-gray-700 rounded-full text-sm font-medium hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all duration-300 cursor-pointer border border-white/40 hover:scale-105"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Back to Blog */}
          <div className="mt-12 pt-8 border-t border-white/40">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-Color hover:text-hoverColor font-bold transition-all group"
            >
              <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
              Back to all posts
            </Link>
          </div>
              </div>
            </article>

            {/* Right Sidebar - Table of Contents */}
            <aside className="hidden xl:block w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Table of Contents */}
                {tableOfContents.length > 0 && (
                  <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <FaBookmark className="text-Color" />
                      Table of Contents
                    </h3>
                    <nav className="space-y-2">
                      {tableOfContents.map((heading) => (
                        <a
                          key={heading.id}
                          href={`#${heading.id}`}
                          className={`block py-2 px-3 rounded-lg text-sm transition-all ${
                            activeHeading === heading.id
                              ? 'bg-gradient-to-r from-Color to-brightColor text-white font-bold shadow-lg'
                              : heading.level === 1
                              ? 'font-bold text-gray-900'
                              : heading.level === 2
                              ? 'pl-6 font-medium text-gray-700'
                              : 'pl-9 text-gray-600'
                          } hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white`}
                        >
                          {heading.text}
                        </a>
                      ))}
                    </nav>
                  </div>
                )}

                {/* Tags Quick Access */}
                {post?.tags && post.tags.length > 0 && (
                  <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <FaTag className="text-Color" />
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 backdrop-blur-xl bg-white/60 text-gray-700 rounded-full text-xs font-medium border border-white/40 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all cursor-pointer"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share Card */}
                <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Share This</h3>
                  <SocialShare
                    url={`https://blogs.nostrahealth.com/${post?.slug}`}
                    title={post?.title || ''}
                    description={post?.excerpt}
                    postId={post?.id}
                  />
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-12">
          <div className="max-w-[1800px] mx-auto px-4">
            <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-12 border border-white/40 shadow-2xl overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-Color/10 via-brightColor/10 to-accentPurple/10"></div>
              <div className="relative z-10 text-center">
                <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-Color via-brightColor to-accentPurple bg-clip-text text-transparent">
                  Transform Your Healthcare Practice
                </h2>
                <p className="text-gray-700 mb-8 max-w-2xl mx-auto text-lg">
                  Join thousands of healthcare professionals using NostraHealth's AI-powered platform
                </p>
                <a
                  href={`${MAIN_SITE}/pricing`}
                  className="inline-block bg-gradient-to-r from-Color to-brightColor text-white px-10 py-4 rounded-full font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  Get Started Today
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="backdrop-blur-xl bg-darkPurple/90 text-white py-8 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-gray-300">
              © {new Date().getFullYear()} NostraHealth. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPost;
